import { forwardRef, useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import moment from 'moment';
import { useSnackbar } from 'notistack';

import { useTheme } from '@mui/material/styles';
import { Button, Divider, IconButton, FormGroup, FormControlLabel, Switch, Stack, Typography } from '@mui/material';
import MenuPopover from './MenuPopover';

import useAuth from '../../hooks/useAuth';
import { useBreakpoints } from '../../hooks/useBreakpoints';
import { MIcon } from '../@material-extend';
import CloseButton from '../features/clockings/ClockingButtons/ClockingButton/CloseButton';

const formatYearMonth = 'YYYYMM';
const MonthPicker = forwardRef((props, ref) => {
  const { checked, disabled, label, maxMonth, minMonth, showCheck, sx, yearMonth, onChange, onCheck } = props;

  const { isPartner } = useAuth();
  const { upLargeMobile } = useBreakpoints();
  const { enqueueSnackbar, closeSnackbar } = useSnackbar();
  const anchorRef = useRef(null);
  const [elapsed, setElapsed] = useState(null);
  const [open, setOpen] = useState(false);
  const [month, setMonth] = useState(yearMonth);
  const theme = useTheme();

  let monthCount = 1;
  const monthCurrent = parseInt(moment(month, formatYearMonth).format('MM'), 10);
  const yearCurrent = parseInt(moment(month, formatYearMonth).format('YYYY'), 10);
  const isPickerPreviousDisabled =
    (!!minMonth &&
      moment(minMonth, formatYearMonth).format(formatYearMonth) ===
        moment(month, formatYearMonth).format(formatYearMonth)) ||
    disabled;
  const isPickerNextDisabled =
    (!!maxMonth &&
      moment(maxMonth, formatYearMonth).format(formatYearMonth) ===
        moment(month, formatYearMonth).format(formatYearMonth)) ||
    disabled;
  const isPopupPreviousDisabled =
    (!!minMonth &&
      (moment(minMonth, formatYearMonth).format('YYYY') === moment(yearCurrent, 'YYYY').format('YYYY') ||
        moment(yearCurrent, 'YYYY').format('YYYY') <= moment(minMonth, formatYearMonth).format('YYYY'))) ||
    disabled;
  const isPopupNextDisabled =
    (!!maxMonth &&
      (moment(maxMonth, formatYearMonth).format('YYYY') === moment(yearCurrent, 'YYYY').format('YYYY') ||
        moment(yearCurrent, 'YYYY').format('YYYY') >= moment(maxMonth, formatYearMonth).format('YYYY'))) ||
    disabled;

  useEffect(() => {
    setMonth(yearMonth);
  }, [yearMonth]);
  // Debug: affiche la stack trace à chaque changement des props (uniquement pour le débogage)
  useEffect(() => {
    console.debug('MonthPicker props updated:', props);
  }, [props]);
  const handleOpen = () => setOpen(true);

  const handleClose = () => {
    setOpen(false);
    setMonth(yearMonth);
  };

  function addMonth() {
    monthCount += 1;
    return monthCount;
  }

  function setMonthCurrent(item) {
    // Récupération de la valeur du mois
    const monthCurrentValue = item.target.attributes.month.value;
    // Ajout d'un zéro devant le mois si nécessaire → (exemple 1 devient 01)
    const twoDigitMonth = String(monthCurrentValue).padStart(2, '0');
    // Création de la date avec le mois formaté
    const newDate = moment(`${yearCurrent}-${twoDigitMonth}-01`, 'YYYY-MM-DD');

    onChange(newDate);
    setOpen(false);
  }

  function getMonthText(number) {
    return moment().month(number).format('MMMM');
  }

  function variantMonth(month) {
    if (month === monthCurrent) {
      return 'contained';
    }
    return '';
  }

  function colorMonth(month) {
    if (month === monthCurrent) {
      return 'primary';
    }
    return 'inherit';
  }

  const nextMonth = () => {
    clearTimeout(elapsed);
    setElapsed(
      setTimeout(() => {
        if (maxMonth && parseInt(moment(month).add(1, 'M').format(formatYearMonth), 10) > parseInt(maxMonth, 10)) {
          return enqueueSnackbar(
            `Vous ne pouvez pas aller au-delà de ${moment(maxMonth, formatYearMonth).format('MMMM')}.`,
            {
              variant: 'warning',
              action: (key) => <CloseButton handleClick={() => closeSnackbar(key)} />
            }
          );
        }
        onChange(moment(month).add(1, 'M'));
      }, 500)
    );
  };

  const previousMonth = () => {
    clearTimeout(elapsed);
    setElapsed(
      setTimeout(() => {
        if (minMonth && parseInt(moment(month).add(-1, 'M').format(formatYearMonth), 10) < parseInt(minMonth, 10)) {
          return enqueueSnackbar(
            `Vous ne pouvez pas aller en deçà de ${moment(minMonth, formatYearMonth).format('MMMM')}.`,
            {
              variant: 'warning',
              action: (key) => <CloseButton handleClick={() => closeSnackbar(key)} />
            }
          );
        }
        onChange(moment(month).add(-1, 'M'));
      }, 500)
    );
  };

  const nextYear = () => {
    let newDate = '';
    if (
      maxMonth &&
      moment(month, formatYearMonth).add(1, 'Y').format(formatYearMonth) >=
        moment(maxMonth, formatYearMonth).format(formatYearMonth)
    ) {
      const monthIndex = parseInt(moment(maxMonth, formatYearMonth).format('MM'), 10) - 1;
      const date = moment(month).add(1, 'Y').set('month', monthIndex);
      newDate = date;
    } else {
      newDate = moment(month).add(1, 'Y');
    }
    setMonth(moment(newDate).format(formatYearMonth));
  };

  const previousYear = () => {
    let newDate = '';
    if (
      minMonth &&
      moment(month, formatYearMonth).add(-1, 'Y').format(formatYearMonth) <=
        moment(minMonth, formatYearMonth).format(formatYearMonth)
    ) {
      const monthIndex = parseInt(moment(minMonth, formatYearMonth).format('MM'), 10) - 1;
      const date = moment(month, formatYearMonth).add(-1, 'Y').set('month', monthIndex).format(formatYearMonth);
      newDate = date;
    } else {
      newDate = moment(month, formatYearMonth).add(-1, 'Y');
    }
    setMonth(moment(newDate).format(formatYearMonth));
  };

  const isMonthSelectDisabled = (number) => {
    const month = number.toString().padStart(2, '0');
    const monthYear = parseInt(moment(`${yearCurrent}${month}`, formatYearMonth).format(formatYearMonth), 10);
    return disabled || (!!minMonth && monthYear < minMonth) || (!!maxMonth && monthYear > maxMonth);
  };

  const Label = () => {
    if (!label) {
      return;
    }

    if (showCheck) {
      const styleActivesSites = {
        mr: 'initial',
        '.MuiTypography-root': { whiteSpace: 'nowrap' }
      };

      return (
        <FormGroup>
          <FormControlLabel
            id="active-sites-switch"
            control={<Switch checked={checked} />}
            label="Actifs sur"
            sx={styleActivesSites}
            onChange={() => onCheck(!checked)}
          />
        </FormGroup>
      );
    }

    return (
      <Typography variant="body2" sx={{ cursor: 'default' }}>
        {label}
      </Typography>
    );
  };

  return (
    <Stack id="month-picker-container" flexDirection="row" alignItems="center" gap={1.5} sx={sx}>
      <Label id="month-picker-label" />

      <Stack id="month-picker" sx={stylePickerWrapper(isPartner, theme, upLargeMobile)} ref={ref}>
        <IconButton id="sites-back-month" disabled={isPickerPreviousDisabled} onClick={previousMonth}>
          <MIcon icon="chevron_left" fontSize={20} />
        </IconButton>
        <Button
          id="monthText"
          ref={anchorRef}
          color="inherit"
          sx={styleButtonMonth}
          disabled={disabled}
          onClick={handleOpen}
        >
          &nbsp;{getMonthText(monthCurrent - 1)} {yearCurrent}&nbsp;
        </Button>
        <IconButton id="sites-next-month" disabled={isPickerNextDisabled} onClick={nextMonth}>
          <MIcon icon="chevron_right" fontSize={20} />
        </IconButton>
      </Stack>

      <MenuPopover
        id="month-picker-calendar"
        sx={{ width: { xs: '100%', md: 400 } }}
        open={open}
        anchorEl={anchorRef.current}
        onClose={handleClose}
      >
        <Stack direction="row" gap={1}>
          <IconButton disabled={isPopupPreviousDisabled} onClick={previousYear}>
            <MIcon icon="chevron_left" fontSize={20} />
          </IconButton>
          <Button disabled color="inherit" sx={{ flexGrow: 1 }}>
            &nbsp;{yearCurrent}&nbsp;
          </Button>
          <IconButton disabled={isPopupNextDisabled} onClick={nextYear}>
            <MIcon icon="chevron_right" fontSize={20} />
          </IconButton>
        </Stack>

        <Divider sx={{ mb: 0.5 }} />

        <table style={{ width: '100%' }}>
          <tbody>
            {[...Array(4)].map((_, indexR) => (
              <tr key={indexR}>
                {[...Array(3)].map((_, indexD) => (
                  <td key={`${addMonth(indexD)}`} style={{ width: '33%' }}>
                    <Button
                      fullWidth
                      size="large"
                      color={colorMonth(monthCount)}
                      variant={variantMonth(monthCount)}
                      month={monthCount}
                      disabled={isMonthSelectDisabled(monthCount)}
                      onClick={(item) => setMonthCurrent(item)}
                    >
                      {getMonthText(monthCount - 1)}
                    </Button>
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </MenuPopover>
    </Stack>
  );
});

const stylePickerWrapper = (isPartner, theme, upLargeMobile) => ({
  flexGrow: isPartner && upLargeMobile ? 0 : 1,
  flexFlow: 'row nowrap',
  background: theme.palette.background.paper,
  border: `1px solid ${theme.palette.divider}`,
  borderRadius: 1,
  minWidth: 208 // Default width for medium sized item (like default search bars)
});

const styleButtonMonth = {
  flexGrow: 1,
  minHeight: 42,
  minWidth: 128,
  textTransform: 'capitalize',
  ':disabled': { color: 'inherit' }
};

MonthPicker.propTypes = {
  yearMonth: PropTypes.string.isRequired,
  minMonth: PropTypes.string,
  maxMonth: PropTypes.string,
  onChange: PropTypes.func.isRequired,
  showCheck: PropTypes.bool,
  checked: PropTypes.bool,
  onCheck: PropTypes.func,
  label: PropTypes.string,
  sx: PropTypes.object,
  disabled: PropTypes.bool
};

export default MonthPicker;
